import { type ReactElement, useCallback, useRef, useMemo, type Dispatch, type SetStateAction, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import type { Filter, MeasurementUnit } from '@/models/Filter';
import Button from '@/components/shared/button/Button';
import dynamic from 'next/dynamic';
import Icon from '@/components/shared/Icons';
import { useApplyFilter } from '@/hooks/useApplyFilter';
import { getLabelForSortType } from '@/utils/filters/sortBy';
import type { Locale } from '@/i18n';
import type { BrandAndChainResponse } from '@/app/api/venue-autocomplete/route';
import getVenueAutoComplete from '@/fetchers/getVenueAutoComplete';
import Loader from '@/components/filters/Loader';
import { isFullMapView } from '@/utils/maps/mapUtils';

const Panel = dynamic(() => import('@/components/shared/panel'), {
  ssr: false,
  loading: () => <Loader className="fixed left-0 top-0 z-30 h-screen w-full bg-background lg:w-480" />
});
const Popper = dynamic(() => import('@/components/shared/popper/Popper'), {
  ssr: false,
  loading: () => (
    <div className="absolute h-96 w-96 bg-background pt-2">
      <Loader className="h-full w-full rounded-lg shadow-3" />
    </div>
  )
});

type Props = {
  venueAutoComplete?: {
    searchTerm: string;
    measurementUnit: MeasurementUnit;
    geo: string | undefined;
    locale: Locale;
    brandCityCountry: BrandAndChainResponse | null;
    setBrandCityCountry: (value: BrandAndChainResponse | null) => void;
  };
  children: ReactElement | ReactElement[];
  selected: Filter;
  setSelected: (selections: Filter) => void;
  appliedFilters: Filter;
  // TODO: narrow this down to the specific filters that are being used
  filters: string[];
  title: string;
  testID?: string;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  panel?: boolean;
  showShadow?: boolean;
  strings: {
    allFilters: string;
    resetAllFilters: string;
    cancelAllFilters: string;
    applyAllFilters: string;
    clear: string;
    apply: string;
  };
  setSortingLabel?: Dispatch<SetStateAction<string>>;
  setSortingType?: Dispatch<SetStateAction<string>>;
  sortOptions?: { label: string; value: string }[];
  sortType?: string;
  sort?: string;
  setSort?: Dispatch<SetStateAction<string>>;
  searchTerm: string;
  filterCount: number;
  setIsRfpBannerVisible: (isRfpBannerVisible: boolean) => void;
  chainsBrandSurface?: boolean;
  aiMode?: boolean;
  isSearchExpanded: boolean;
  setIsSearchExpanded: (v: boolean) => void;
};

const emptyMinMaxValue = {
  minimum: null,
  maximum: null
};

function defaultValues(filter: Filter, key: string) {
  switch (key) {
    case 'VENUE_TYPE':
    case 'PLANNER_TAGS':
      return [];
    case 'RATE_HISTORY':
      return {
        maximum: 500,
        values: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
      };
    case 'MULTI_FILTER':
      return {
        CHAIN: [],
        BRAND: [],
        VENUE_NAME: []
      };
    case 'SECOND_LARGEST_ROOM':
    case 'TOTAL_MEETING_SPACE':
    case 'LARGEST_ROOM':
      return emptyMinMaxValue;
    case 'CANCELLED_SPACE':
      return false;
    case 'VENUE_DETAILS':
      return (
        filter.VENUE_DETAILS?.filter(
          v =>
            v !== 'INSTANT_BOOK_ENABLED' &&
            v !== 'HAS_3D_DIAGRAMS' &&
            v !== 'PASSKEY_ENABLED' &&
            v !== 'SOCIAL_TABLES_ENABLED' &&
            v !== 'FAST_RESPONDER'
        ) || []
      );
    case 'AMENITIES':
      return filter.AMENITIES?.filter(a => a !== 'ENVIRONMENTALLY_FRIENDLY') || [];
    default:
      return null;
  }
}

/**
 * Wrapper for filters that handles the apply and reset buttons
 */
export default function ApplyResetWrapper({
  venueAutoComplete,
  title,
  children,
  selected,
  setSelected,
  appliedFilters,
  filters,
  testID,
  isOpen,
  setIsOpen,
  panel = false,
  showShadow = false,
  strings,
  setSortingLabel,
  sortOptions,
  setSortingType,
  sort,
  setSort,
  sortType,
  filterCount,
  setIsRfpBannerVisible,
  chainsBrandSurface,
  aiMode = false,
  isSearchExpanded,
  setIsSearchExpanded
}: Props) {
  const pathName = usePathname();
  const params = useSearchParams();

  const { applyFilters } = useApplyFilter();
  // Removed '/results/' from path
  const finalPath = pathName.split('/results/')[1];
  const triggerRef = useRef<HTMLButtonElement>(null);

  const [url, setUrl] = useState<string>(
    typeof window === 'undefined' ? '' : window.location.pathname + window.location.search
  );

  const { allFilters, resetAllFilters, cancelAllFilters, applyAllFilters, clear, apply } = strings;

  const onReset = useCallback(async () => {
    document.body.classList.remove('overflow-hidden');
    const updatedFilters = { ...selected };
    filters.forEach(filter => {
      delete updatedFilters[filter];
    });
    setIsOpen(false);
    setIsSearchExpanded(false);
    setIsRfpBannerVisible(false);
    if (sortOptions && setSortingLabel) {
      const label = getLabelForSortType(sortOptions, 'CVENT_PICKS');
      setSortingLabel(label);
    }
    if (setSortingType) {
      setSortingType('CVENT_PICKS' as string);
    }
    if (setSort) {
      setSort('CVENT_PICKS');
    }
    await applyFilters(finalPath, updatedFilters, true, 'CVENT_PICKS');
  }, [
    applyFilters,
    filters,
    finalPath,
    selected,
    setIsOpen,
    setIsRfpBannerVisible,
    setIsSearchExpanded,
    setSort,
    setSortingLabel,
    setSortingType,
    sortOptions
  ]);

  const onClear = useCallback(async () => {
    const updatedFilters = { ...selected };
    filters.forEach(filter => {
      // Clear out the values
      updatedFilters[filter] = defaultValues(selected, filter);
    });

    setIsOpen(false);
    setIsSearchExpanded(false);
    document.body.classList.remove('overflow-hidden');
    setSelected(updatedFilters);
    await applyFilters(finalPath, updatedFilters, true, sort);
  }, [applyFilters, filters, finalPath, selected, setIsOpen, setIsSearchExpanded, setSelected, sort]);

  const onApply = useCallback(async () => {
    setIsOpen(false);
    setIsSearchExpanded(false);
    document.body.classList.remove('overflow-hidden');
    if (sortOptions && setSortingLabel) {
      const label = getLabelForSortType(sortOptions, sort as string);
      setSortingLabel(label);
    }
    if (setSortingType) {
      setSortingType(sort as string);
    }
    await applyFilters(finalPath, selected, true, sort);
  }, [
    setIsOpen,
    setIsSearchExpanded,
    sortOptions,
    setSortingLabel,
    setSortingType,
    applyFilters,
    finalPath,
    selected,
    sort
  ]);

  const onDismiss = useCallback(() => {
    if (setSortingType) {
      setSortingType(sortType as string);
    }
    if (setSort) {
      setSort(sortType as string);
    }
    setIsOpen(false);
    setSelected(appliedFilters);
    document.body.classList.remove('overflow-hidden');
  }, [appliedFilters, setIsOpen, setSelected, setSortingType, setSort, sortType]);

  const onOpen = useCallback(async () => {
    setIsOpen(true);
    if (
      venueAutoComplete &&
      (venueAutoComplete.brandCityCountry == null || url !== window.location.pathname + window.location.search)
    ) {
      venueAutoComplete.setBrandCityCountry(
        await getVenueAutoComplete(
          venueAutoComplete.searchTerm,
          venueAutoComplete.measurementUnit,
          venueAutoComplete.geo,
          venueAutoComplete.locale,
          appliedFilters,
          isFullMapView(params) // We need to pass the Map view too. As the chains/brands/venues will be different for full map
        )
      );
      setUrl(window.location.pathname + window.location.search);
    }
  }, [appliedFilters, params, setIsOpen, url, venueAutoComplete]);

  const hasError = useMemo((): boolean => Object.keys(selected).some(f => selected[f].error), [selected]);

  return (
    <div>
      <div className={`${aiMode ? 'pt-2' : ''} flex items-center pr-2 lg:hidden`}>
        <Button
          appearance="ghost"
          icon={<Icon id="all-filters" className="h-6 w-6 rotate-0 fill-interactive-50" />}
          onClick={onOpen}
          aria-label={allFilters}
        />
        {aiMode && filterCount > 0 && <span className="-ml-2.5 -mt-7 h-2 w-2 rounded-full bg-tertiary-60" />}
      </div>
      <div className={`hidden lg:flex ${aiMode ? 'mr-4' : ''}`}>
        <Button
          onClick={isOpen ? onDismiss : onOpen}
          text={title}
          aria-label={title}
          ref={triggerRef}
          variant="brand"
          size={isSearchExpanded ? 'm' : 's'}
          // eslint-disable-next-line no-nested-ternary
          appearance={filterCount > 0 ? 'filled' : !isSearchExpanded ? 'ghost' : 'lined'}
          className={`group rounded-full ${
            filterCount > 0 && !isSearchExpanded && !isOpen ? '!bg-brand-20 !text-brand-70' : ''
          } ${isOpen ? '!bg-brand-70 !text-background' : ''}`}
          icon={
            <Icon
              id={`${panel ? 'all-filters' : 'chevron-down'}`}
              className={`h-4 w-4 ${filterCount > 0 || isOpen ? 'fill-background' : 'fill-brand-60'} ${
                !isSearchExpanded ? '' : 'group-hover:fill-background'
              } ${filterCount > 0 && !isSearchExpanded && !isOpen ? 'fill-brand-60' : ''}`}
            />
          }
          iconAlign={`${panel ? 'start' : 'end'}`}
        />
      </div>
      {!panel && isOpen && (
        <Popper
          className="rounded-xl"
          triggerRef={triggerRef}
          isOpen={isOpen}
          preventOverflow={false}
          hasShadow
          onDismiss={onDismiss}
          placement="bottom-start"
          onPointerLeave={_e => document.body.classList.remove('overflow-hidden')}
          onPointerEnter={_e => document.body.classList.add('overflow-hidden')}
        >
          <div className="w-96 rounded-xl bg-background">
            <div
              className={`${chainsBrandSurface ? '' : 'overflow-y-scroll'} max-h-[400px] px-4 pt-4`}
              data-cvent-id={`${testID}-filter-wrapper`}
            >
              <div className="flex flex-col gap-2">{children}</div>
            </div>
            <div
              className={`bottom-0 flex h-16 w-full items-center justify-end gap-4 px-4 ${
                showShadow ? 'shadow-4' : ''
              }`}
            >
              <div className="hidden sm:block">
                <Button
                  text={clear}
                  onClick={onClear}
                  aria-label={clear}
                  size="s"
                  testID={`${testID}-filter-clear-button`}
                />
              </div>
              <Button
                text={apply}
                onClick={onApply}
                disabled={hasError}
                appearance="filled"
                aria-label={apply}
                size="s"
                testID={`${testID}-filter-apply-button`}
              />
            </div>
          </div>
        </Popper>
      )}
      {panel && isOpen && (
        <Panel isOpen={isOpen} onDismiss={onDismiss}>
          <div
            className="fixed flex h-[9%] w-full items-center justify-between px-6 shadow-2 lg:max-w-480"
            data-cvent-id={`${testID}-filter-wrapper`}
          >
            <div className="font-primary text-d-sm">{allFilters}</div>
            <Button
              icon={<Icon id="x" className="h-6 w-6 fill-neutral-60" />}
              onClick={onDismiss}
              appearance="ghost"
              aria-label={cancelAllFilters}
              testID={`${testID}-cancel-button`}
            />
          </div>
          <div className="absolute left-0 top-[9%] h-[82%] w-full overflow-y-scroll p-4 lg:max-w-480">{children}</div>
          <div className="fixed bottom-0 flex h-[9%] w-full items-center justify-end gap-4 bg-background px-6 shadow-2 lg:max-w-480">
            <Button
              text={resetAllFilters}
              onClick={onReset}
              appearance="ghost"
              variant="danger"
              aria-label={resetAllFilters}
              testID={`${testID}-reset-filters-button`}
            />
            <div className="hidden sm:block">
              <Button
                text={cancelAllFilters}
                onClick={onDismiss}
                aria-label={cancelAllFilters}
                testID={`${testID}-cancel-filters-button`}
              />
            </div>
            <Button
              text={applyAllFilters}
              onClick={onApply}
              disabled={hasError}
              appearance="filled"
              aria-label={applyAllFilters}
              testID={`${testID}-apply-filters-button`}
            />
          </div>
        </Panel>
      )}
    </div>
  );
}
